/**
 * Phase 7 — wraps a section of the app to catch React rendering errors
 * and report them to Sentry. Falls back to a friendly retry UI so a
 * single section's crash doesn't take down the whole page.
 */
import React from 'react';
import * as Sentry from '@sentry/react';

function ModernFallback({ resetError }) {
  return (
    <div data-testid="sentry-fallback" style={{ margin: 12, padding: 24, background: '#FFFFFF', border: '1px solid rgba(15,23,18,0.08)', borderRadius: 14, fontFamily: 'Inter, -apple-system, sans-serif', boxShadow: '0 4px 16px rgba(15,23,18,0.06)' }}>
      <p style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#0F1712' }}>Something went wrong</p>
      <p style={{ margin: '6px 0 16px', fontSize: 14, color: '#5B6B60' }}>This section didn't load. Please try again.</p>
      <button onClick={resetError} style={{ background: '#059669', color: '#FFFFFF', border: 'none', borderRadius: 999, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }} data-testid="sentry-fallback-retry">
        Try again
      </button>
    </div>
  );
}

// Boundary can render outside ThemeProvider, so read the <html> attribute directly.
function DefaultFallback({ error, resetError }) {
  if (typeof document !== 'undefined' && document.documentElement.dataset.theme === 'modern') {
    return <ModernFallback resetError={resetError} />;
  }
  return (
    <div
      data-testid="sentry-fallback"
      style={{
        padding: '24px',
        margin: '12px',
        fontFamily: 'JetBrains Mono, monospace',
        background: '#0C0612',
        color: '#FF3B30',
        border: '1px solid #FF3B30',
        borderRadius: 0,
      }}
    >
      <p style={{ marginTop: 0 }}>// SOMETHING_BROKE</p>
      <p style={{ color: '#A3C7A8' }}>{error?.message || 'Unknown error'}</p>
      <button
        onClick={resetError}
        style={{
          background: '#39FF14',
          color: '#050003',
          border: 'none',
          padding: '8px 16px',
          fontFamily: 'JetBrains Mono, monospace',
          cursor: 'pointer',
        }}
        data-testid="sentry-fallback-retry"
      >
        RETRY
      </button>
    </div>
  );
}

export function SentryBoundary({ children, section, fallback }) {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback || (({ error, resetError }) => <DefaultFallback error={error} resetError={resetError} />)}
      beforeCapture={(scope) => {
        if (section) scope.setTag('error_boundary_section', section);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
